import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddFilled,
  CheckboxPersonRegular,
  EyeFilled,
  OpenRegular,
  PeopleRegular,
  RenameRegular,
  SettingsRegular,
} from '@fluentui/react-icons';

import {
  ItemPage,
  PERIODIC_REVIEW_QUERY_PARAM,
  PERIODIC_REVIEW_QUERY_VALUE,
  CreateVersionDialog,
  NavOverview,
  TResolvedItemsMap,
  useExternalLinkNavAction,
  PeriodicReviewDialog,
} from '@modules/items';
import { AccessType, TPermissions, AccessDialog, TAccessDialogState, useCanEdit } from '@modules/access';

import { TFile, TFileVersion } from '../types';
import Header from './Header';
import Content from './Content';
import NavFileInsights from './NavFileInsights';
import { TReplica } from '@modules/replicas';
import {
  ViewApprovalDialog,
  TViewApprovalDialogState,
  UpdateApprovalDialog,
  TUpdateApprovalDialogState,
  ApprovalStatus,
  CreateApprovalDialog,
} from '@modules/approvals';
import { LayoutContext, ToastContext } from '@shared/contexts';
import { resolveApprovedVersionId } from '@shared/helpers';
import { useSearchParams } from '@shared/hooks';
import { TOverflowButtonParam, OptionsDialog } from '@shared/components';
import { SignInContext } from '@modules/users';
import { TOptionsDialogState } from '@shared/types';
import { TERMINOLOGY } from '@shared/i18n';
import { useImpactAnalysisNavAction } from '@modules/impacts';
import ItemConfigDialog from '@modules/documents/components/dialogs/ItemConfigDialog/ItemConfigDialog';

type TProps = {
  file: TFile;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
  replica?: TReplica;
};

const FileContainer = ({ file, permissions, resolvedItems, replica }: TProps) => {
  const itemType = 'FILE';
  const { organization } = useContext(SignInContext);
  const { canEdit } = useCanEdit(permissions.accessRight);
  const { canEdit: canEditWithoutAuthorGroup } = useCanEdit(permissions.accessRight, { authorGroup: false });
  const isImpactAnalysisEnabled = organization.featureSettings.impactAnalysis?.enabled;
  const isExternalLinkEnabled = organization.featureSettings.externalLink?.enabled;
  const { show } = useContext(ToastContext);
  const { layoutState } = useContext(LayoutContext);

  const { searchParams, change, changeMany } = useSearchParams();
  const view = searchParams.get('view');
  const periodicReviewParam = searchParams.get(PERIODIC_REVIEW_QUERY_PARAM);

  let version: TFileVersion | undefined = undefined;
  if (view === 'approved') {
    const approvedVersionId = resolveApprovedVersionId({
      versions: file.versions,
      approvedVersionId: file.stats.approvedVersionId,
      approvals: file.approvals,
    });
    if (approvedVersionId) version = file.versions.find((version) => version.id === approvedVersionId);
  } else if (view) version = file.versions.find((version) => version.id === view);
  version = version || file.versions[0];

  const [isCreateVersionOpen, setIsCreateVersionOpen] = useState<boolean>(false);
  const handleCreateVersion = () => setIsCreateVersionOpen(true);

  const handleShowAccess = () =>
    setAccessDialogState({
      isOpen: true,
      id: file._id,
      nodeType: 'FILE',
      name: file.name,
    });

  // --- NAV:
  const navigate = useNavigate();

  const [isOpenDocumentConfig, setIsOpenDocumentConfig] = useState(false);
  const [isPeriodicReviewOpen, setIsPeriodicReviewOpen] = useState(false);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const [optionsDialogState, setOptionsDialogState] = useState<TOptionsDialogState | undefined>(undefined);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [viewApprovalDialogState, setViewApprovalDialogState] = useState<TViewApprovalDialogState | undefined>(
    undefined
  );
  const [updateApprovalDialogState, setUpdateApprovalDialogState] = useState<TUpdateApprovalDialogState | undefined>(
    undefined
  );

  const { impactAnalysisNavActionButton } = useImpactAnalysisNavAction({ version, setOptionsDialogState, permissions });
  const { externalLinkNavActionButton } = useExternalLinkNavAction({ item: replica ?? file });

  useEffect(() => {
    if (!replica && periodicReviewParam === PERIODIC_REVIEW_QUERY_VALUE) setIsPeriodicReviewOpen(true);
  }, [periodicReviewParam, replica]);

  const handleSetPeriodicReviewOpen = (isOpen: boolean) => {
    setIsPeriodicReviewOpen(isOpen);
    if (!isOpen) changeMany({ [PERIODIC_REVIEW_QUERY_PARAM]: undefined });
  };

  const openPeriodicReviewDialog = () => {
    if (replica) return;
    setIsPeriodicReviewOpen(true);
    changeMany({ [PERIODIC_REVIEW_QUERY_PARAM]: PERIODIC_REVIEW_QUERY_VALUE });
  };

  const handleCreateVersionNav = () => {
    if (
      file.versions.length > 0 &&
      file.approvals.find((approval) => !approval.dateCompleted && approval.versionId === file.versions[0].id)
    )
      show('warning', 'File has a pending approval request with version increase!');
    else handleCreateVersion();
  };

  const navActions: TOverflowButtonParam[] = useMemo(() => {
    const navActionsList: TOverflowButtonParam[] = [];

    const latestVersion = file.versions[0];
    const isViewingLatest = version === latestVersion;

    if (replica)
      navActionsList.push({
        children: `View ${TERMINOLOGY.master.lower}`,
        icon: <OpenRegular />,
        onClick: () => navigate(`/file/${file._id}`),
      });

    if (isViewingLatest) {
      if (canEdit && !replica)
        navActionsList.push({
          children: 'New version',
          icon: <AddFilled />,
          appearance: 'primary' as const,
          onClick: handleCreateVersionNav,
        });

      if (isImpactAnalysisEnabled) navActionsList.push(impactAnalysisNavActionButton);
    } else {
      navActionsList.push({
        children: 'View latest',
        icon: <EyeFilled />,
        onClick: () => change('view'),
      });
    }

    if (
      !replica &&
      canEditWithoutAuthorGroup &&
      !file.approvals.some(
        (approval) =>
          approval.versionId === version.id && (!approval.dateCompleted || approval.status === ApprovalStatus.APPROVED)
      )
    )
      navActionsList.push({
        children: 'Request approval',
        icon: <CheckboxPersonRegular />,
        onClick: () => {
          // show('info', 'Approvals for custom files are not activated yet. Please contact your administrator.');
          setIsApprovalDialogOpen(true);
        },
      });

    if (permissions.accessRight >= AccessType.MANAGE) {
      navActionsList.push({
        children: 'Manage access',
        icon: <PeopleRegular />,
        onClick: handleShowAccess,
      });
    }

    if (layoutState) {
      navActionsList.push({
        children: 'Rename',
        icon: <RenameRegular />,
        value: 'rename',
        disabled: !canEditWithoutAuthorGroup,
        onClick: layoutState.onRename,
      });
    }

    /**
     * Deprecated: downloading is now handled in `files/components/Content.tsx`. Can be removed later.
     */
    // const versionLabel = version ? (isTDraft(version) ? 'draft' : `v${version.id}`) : '';
    // navActionsList.push({
    //   children: `Export ${versionLabel}`,
    //   icon: <ArrowDownloadRegular />,
    //   value: 'export',
    //   onClick: handleExport,
    //   disabled: !version,
    // });

    if (isExternalLinkEnabled) navActionsList.push(externalLinkNavActionButton);

    if (!replica) {
      navActionsList.push({
        children: 'Settings',
        value: 'settings',
        icon: <SettingsRegular />,
        onClick: () => setIsOpenDocumentConfig(true),
      });
    }

    return navActionsList;
  }, [
    version,
    permissions,
    layoutState,
    file.approvals,
    isImpactAnalysisEnabled,
    isExternalLinkEnabled,
    externalLinkNavActionButton,
  ]);

  const navTabs = {
    Overview: (
      <NavOverview
        item={file}
        replica={replica}
        actions={navActions}
        itemVersion={version && { type: file.type, item: file, version }}
        permissions={permissions}
        setViewApprovalDialogState={setViewApprovalDialogState}
        openPeriodicReviewDialog={openPeriodicReviewDialog}
      />
    ),
    Insights: <NavFileInsights file={file} version={version} replica={replica} />,
  };
  return (
    <>
      <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
      <OptionsDialog state={optionsDialogState} setState={setOptionsDialogState} />
      <CreateVersionDialog
        isOpen={isCreateVersionOpen}
        setIsOpen={setIsCreateVersionOpen}
        item={file}
        resolvedItems={resolvedItems}
      />
      <ItemConfigDialog
        isOpen={isOpenDocumentConfig}
        setIsOpen={setIsOpenDocumentConfig}
        item={file}
        isDoc={false}
        version={version}
        permissions={permissions}
        openPeriodicReviewDialog={openPeriodicReviewDialog}
      />
      {!replica && (
        <PeriodicReviewDialog
          isOpen={isPeriodicReviewOpen}
          setIsOpen={handleSetPeriodicReviewOpen}
          item={file}
          itemType="FILE"
          permissions={permissions}
        />
      )}
      <CreateApprovalDialog
        isOpen={isApprovalDialogOpen}
        setIsOpen={setIsApprovalDialogOpen}
        master={file}
        itemType={itemType}
        versionId={version.id}
        setViewApprovalDialogState={setViewApprovalDialogState}
      />

      <ViewApprovalDialog
        state={viewApprovalDialogState}
        setState={setViewApprovalDialogState}
        setUpdateApprovalDialogState={setUpdateApprovalDialogState}
        item={file}
        replica={replica}
        itemType={itemType}
        permissions={permissions}
      />
      <UpdateApprovalDialog
        state={updateApprovalDialogState}
        setState={setUpdateApprovalDialogState}
        setViewApprovalDialogState={setViewApprovalDialogState}
        item={file}
        itemType={itemType}
      />
      <ItemPage
        item={file}
        permissions={permissions}
        resolvedItems={resolvedItems}
        version={version}
        replica={replica}
        navTabs={navTabs}
        renderHeader={(selectedTab, setSelectedTab) => (
          <Header
            file={file}
            version={version}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            permissions={permissions}
            onCreateVersion={handleCreateVersion}
            // Access dialog handler
            handleShowAccess={handleShowAccess}
          />
        )}
        content={<Content file={file} version={version} permissions={permissions} replica={replica} />}
        setViewApprovalDialogState={setViewApprovalDialogState}
      />
    </>
  );
};

export default FileContainer;
